/**
 * Описания курсов приходят из БД и могут содержать разметку. Показывать их
 * текстом нельзя (видны теги), а отдавать в dangerouslySetInnerHTML как есть —
 * это stored XSS: описание правит не конечный пользователь, но любой, кто
 * получит доступ к редактированию курса, выполнит скрипт у всех посетителей
 * каталога. Поэтому разметка пропускается через белый список.
 *
 * Санитайзер намеренно свой: сторонние библиотеки в этот milestone не ставим
 * (см. CLAUDE.md — no new npm packages).
 */

// Только инлайновое форматирование, абзацы, списки и ссылки — всё, что нужно
// описанию курса. Всё остальное разворачивается в текст.
const ALLOWED_TAGS = new Set([
  'P', 'BR', 'UL', 'OL', 'LI', 'STRONG', 'B', 'EM', 'I', 'U', 'A', 'SPAN',
]);

// Теги, которые нельзя развернуть в текст: их содержимое — не контент, а код.
const DROPPED_WITH_CONTENT = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE']);

const ALLOWED_URL_SCHEMES = ['http:', 'https:', 'mailto:'];

const isSafeHref = (raw: string): boolean => {
  const value = raw.trim();
  if (!value) return false;
  // Относительные ссылки (/courses, ./x, #anchor) схемы не имеют и безопасны.
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value)) {
    const scheme = value.slice(0, value.indexOf(':') + 1).toLowerCase();
    return ALLOWED_URL_SCHEMES.includes(scheme);
  }
  return true;
};

/** Заменяет узел его же дочерними узлами, сохраняя текст. */
const unwrap = (node: Element) => {
  const parent = node.parentNode;
  if (!parent) return;
  while (node.firstChild) parent.insertBefore(node.firstChild, node);
  parent.removeChild(node);
};

const cleanElement = (el: Element) => {
  const tag = el.tagName.toUpperCase();

  if (DROPPED_WITH_CONTENT.has(tag)) {
    el.remove();
    return;
  }

  // Обход детей до возможного unwrap: после него узлы переезжают к родителю.
  Array.from(el.children).forEach(cleanElement);

  if (!ALLOWED_TAGS.has(tag)) {
    unwrap(el);
    return;
  }

  // Срезаем все атрибуты — этим уходят и on*-обработчики, и style, и
  // data-*. Ссылке href возвращаем отдельно, после проверки схемы.
  const href = tag === 'A' ? el.getAttribute('href') : null;
  Array.from(el.attributes).forEach((attr) => el.removeAttribute(attr.name));
  if (href && isSafeHref(href)) {
    el.setAttribute('href', href.trim());
    el.setAttribute('target', '_blank');
    el.setAttribute('rel', 'noopener noreferrer');
  }
};

const parseBody = (html: string): HTMLElement =>
  new DOMParser().parseFromString(html, 'text/html').body;

/**
 * Возвращает HTML, безопасный для dangerouslySetInnerHTML: только теги из
 * белого списка, без атрибутов (кроме проверенного href у ссылок).
 */
export const sanitizeHtml = (html: string | null | undefined): string => {
  if (!html) return '';
  const body = parseBody(html);
  Array.from(body.children).forEach(cleanElement);
  return body.innerHTML;
};

/**
 * Текстовое содержимое разметки — для поиска и других мест, где нужен plain
 * text. Блочные теги дают пробел, иначе «<li>Первый</li><li>Второй</li>»
 * склеивалось бы в «ПервыйВторой» и ломало поиск по слову.
 */
export const htmlToText = (html: string | null | undefined): string => {
  if (!html) return '';
  const body = parseBody(html.replace(/<\/?(p|br|li|ul|ol|div|tr|td|h[1-6])\b[^>]*>/gi, ' '));
  return (body.textContent || '').replace(/\s+/g, ' ').trim();
};
